/**
 * Print Agent Service
 * Service to communicate with Print Agent Windows Service for thermal printing
 *
 * Architecture:
 * 1. Frontend calls Backend API (/api/escpos/generate) to generate ESC/POS bytes. The
 *    server applies the restaurant's ticket settings (e.g. MostrarDesgloseIVA).
 * 2. Frontend sends raw bytes to Print Agent for printing.
 * 3. If the server doesn't answer, the agent builds the ticket itself from the structured
 *    data with its OWN generator (PrintAgent Services/EscPosService.cs), which has to stay
 *    in step with the server's EscPosGeneratorService. Whatever the server reads from the
 *    restaurant settings must travel in that data (see sendStructured).
 */

import { PRINT_AGENT_CONFIG, PrintMethod, checkPrintMethodAvailable } from '../config/printConfig';
import { Order, OrderItem, OrderDiscount } from '../types/orders';
import { formatInTz, DEFAULT_TIMEZONE } from '../utils/dateHelpers';

/**
 * Tope para pedirle los bytes del ticket al servidor. Corto a propósito: cuando no hay
 * servidor, lo que sigue es que el agente arme el ticket él mismo, y eso no puede esperar
 * a que el navegador se rinda solo.
 */
const ESCPOS_GENERATE_TIMEOUT_MS = 4000;

/**
 * Pie del ticket. En una orden tomada sin conexión antepone su folio provisional
 * ("SIN RED 7") y dice que el folio real está pendiente: `OrderNumber` no sirve para eso
 * —es un número y la orden todavía no tiene el suyo—, y un ticket sin folio deja al
 * cliente y a la caja sin cómo referirse a esa cuenta.
 */
const offlineFolioFooter = (order: Pick<Order, 'offlineFolioLabel'>, footer: string): string =>
  order.offlineFolioLabel
    ? `${order.offlineFolioLabel}\nFolio pendiente de sincronizar\n\n${footer}`
    : footer;

// Group identical items by product+variants+unitPrice, summing quantities and totals
const groupOrderItems = (items: OrderItem[]) => {
  const map = new Map<string, { item: OrderItem; qty: number; total: number }>();
  for (const item of items) {
    const variantsKey = (item.variants || []).map(v => v.variantName).sort().join(',');
    const key = `${item.productName}|${variantsKey}|${item.unitPrice}`;
    const existing = map.get(key);
    if (existing) {
      existing.qty += item.quantity;
      existing.total += item.total;
    } else {
      map.set(key, { item, qty: item.quantity, total: item.total });
    }
  }
  return Array.from(map.values());
};

/**
 * Holds the active restaurant timezone for ticket timestamps. The print
 * services run outside the React tree, so the tz is pushed in via
 * `setPrintAgentTimeZone` on settings load (see PrintServicesSettingsSync).
 * Falls back to America/Mexico_City to preserve historical behavior.
 */
let activeTimeZone: string = DEFAULT_TIMEZONE;

/**
 * Sets the timezone used by all module-level print services in this file.
 * Idempotent; safe to call on every settings change.
 */
export const setPrintAgentTimeZone = (tz: string): void => {
  activeTimeZone = tz || DEFAULT_TIMEZONE;
};

/**
 * Returns "now" formatted as a local ISO string (no Z suffix) in the
 * configured restaurant timezone. The backend's ESC/POS generator treats
 * this as the wall-clock time to print on the ticket.
 */
function getLocalDateTimeString(): string {
  return formatInTz(new Date(), activeTimeZone, 'YYYY-MM-DDTHH:mm:ss');
}

/**
 * Setting `MostrarDesgloseIVA` del restaurante, empujado igual que la zona horaria
 * (PrintServicesSettingsSync). El servidor lo lee por su cuenta y no necesita esto: viaja
 * solo en los datos con los que el agente arma el ticket él solo (sendStructured), que de
 * otro modo imprimiría Subtotal e IVA aunque el restaurante los haya apagado. Default
 * true: es el default del setting.
 */
const DEFAULT_SHOW_TAX_BREAKDOWN = true;
let activeShowTaxBreakdown = DEFAULT_SHOW_TAX_BREAKDOWN;

export const setPrintAgentShowTaxBreakdown = (show: boolean): void => {
  activeShowTaxBreakdown = show;
};

/**
 * Vuelve al default. Al cerrar sesión: el último valor conocido era de ese usuario y
 * restaurante, y la siguiente sesión en esta pestaña puede ser de otro.
 */
export const resetPrintAgentShowTaxBreakdown = (): void => {
  activeShowTaxBreakdown = DEFAULT_SHOW_TAX_BREAKDOWN;
};

/**
 * Nombre del tipo de ticket para el agente, por la numeración del servidor
 * (EscPosReceiptType) que llevan los datos. El enum del agente tiene un `Receipt` de más en
 * el 3: mandado como número, del 3 en adelante el agente armaba otro ticket (el arqueo salía
 * como "TEST DE IMPRESION" y el retiro como cierre de caja, sin monto). Por nombre llega bien
 * a cualquier versión del agente: todas leen el enum como texto, igual que el 'Raw' de
 * sendRawBytes.
 */
const AGENT_PRINT_TYPES = ['PreCheck', 'Payment', 'KitchenOrder', 'Test', 'CashCount', 'CashRegisterClosure', 'CashMovement'];

export interface PrintAgentResult {
  success: boolean;
  message: string;
  jobId?: string;
  error?: string;
}

export interface PrintAgentHealth {
  status: string;
  version: string;
  timestamp: string;
  printer: {
    available: boolean;
    name?: string;
    status: string;
    queuedJobs: number;
  };
}

/**
 * Print Agent Service for Windows PCs
 * Uses backend API for ESC/POS generation; the Print Agent prints, and only builds the
 * ticket itself when the server doesn't answer (printViaAgent).
 */
export class PrintAgentService {
  /**
   * Get the current Print Agent URL
   */
  private get printAgentUrl(): string {
    return `${PRINT_AGENT_CONFIG.endpoint}:${PRINT_AGENT_CONFIG.port}`;
  }

  /**
   * Get the backend API base URL for ESC/POS generation
   */
  private get apiBaseUrl(): string {
    // Remove /api suffix if present, as we'll add the full path
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://localhost:7103/api';
    return baseUrl.replace(/\/api$/, '');
  }

  /**
   * Check if Print Agent is available
   */
  async isAvailable(): Promise<boolean> {
    return await checkPrintMethodAvailable(PrintMethod.PRINT_AGENT);
  }

  /**
   * Get health status
   */
  async getHealth(): Promise<PrintAgentHealth | null> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), PRINT_AGENT_CONFIG.timeout);

      const response = await fetch(`${this.printAgentUrl}/api/print/health`, {
        method: 'GET',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        return await response.json();
      }

      return null;
    } catch (error) {
      console.warn('Print Agent health check failed:', error);
      return null;
    }
  }

  /**
   * Generate ESC/POS bytes using backend API
   */
  private async generateEscPosBytes(requestData: any): Promise<string | null> {
    // Con un tope corto: si no hay servidor, esta petición se queda colgada el default del
    // navegador (minutos) antes de que el agente pueda imprimir. La comanda no puede
    // esperar a que el navegador se rinda.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), ESCPOS_GENERATE_TIMEOUT_MS);
    try {
      const token = localStorage.getItem('accessToken');
      const endpoint = `${this.apiBaseUrl}/api/escpos/generate`;

      console.log('🖨️ Generating ESC/POS bytes via backend API');

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify(requestData),
        signal: controller.signal,
      });

      if (!response.ok) {
        console.error('Failed to generate ESC/POS bytes:', response.status, response.statusText);
        return null;
      }

      const result = await response.json();

      if (result.success && result.data) {
        console.log(`✅ Generated ${result.byteCount} bytes`);
        return result.data; // Base64 encoded bytes
      }

      console.error('ESC/POS generation failed:', result.message);
      return null;
    } catch (error) {
      console.error('Error generating ESC/POS bytes:', error);
      return null;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Imprime por el agente. Primero pide los bytes al servidor —que es quien define cómo se
   * ve el ticket— y, si no contesta, le manda los mismos datos al agente para que él arme
   * el ESC/POS con su propio generador.
   *
   * Ese segundo camino es lo que hace posible comandar y cobrar sin internet: el ticket
   * sale casi igual (lo dibuja otro generador), y sin él la caja offline se quedaría sin
   * comanda para la cocina ni ticket para el cliente.
   */
  private async printViaAgent(requestData: any): Promise<PrintAgentResult> {
    const bytes = await this.generateEscPosBytes(requestData);
    if (bytes) return this.sendRawBytes(bytes);

    console.warn('🖨️ Sin bytes del servidor: el agente arma el ticket con los datos');
    return this.sendStructured(requestData);
  }

  /**
   * Manda al agente los datos del ticket (él genera el ESC/POS). Aquí se adaptan a lo que
   * el agente espera: el tipo por nombre (AGENT_PRINT_TYPES) y el desglose de IVA, que el
   * servidor lee por su cuenta y el agente no tiene cómo.
   */
  private async sendStructured(requestData: any): Promise<PrintAgentResult> {
    return this.postToAgent({
      ...requestData,
      Type: AGENT_PRINT_TYPES[requestData.Type] ?? requestData.Type,
      Receipt: { ...requestData.Receipt, ShowTaxBreakdown: activeShowTaxBreakdown },
    });
  }

  /**
   * Send raw bytes to Print Agent for printing
   */
  private async sendRawBytes(base64Data: string): Promise<PrintAgentResult> {
    return this.postToAgent({ type: 'Raw', rawData: base64Data });
  }

  /** POST al agente en localhost, compartido por los dos caminos de impresión. */
  private async postToAgent(payload: unknown): Promise<PrintAgentResult> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), PRINT_AGENT_CONFIG.timeout);

      const response = await fetch(`${this.printAgentUrl}/api/print`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const data = await response.json();

      if (response.ok) {
        return {
          success: data.success,
          message: data.message,
          jobId: data.jobId,
        };
      } else {
        return {
          success: false,
          message: data.message || 'Error al imprimir',
          error: data.error,
        };
      }
    } catch (error: any) {
      console.error('Error communicating with Print Agent:', error);
      return {
        success: false,
        message: 'No se pudo conectar con el servicio de impresión',
        error: error.message || 'Connection error',
      };
    }
  }

  /**
   * Print a pre-check/pre-bill receipt
   */
  async printPreCheck(
    order: Order,
    restaurantName: string,
    restaurantAddress: string | undefined,
    restaurantPhone: string | undefined,
    tableName: string,
    waiterName: string,
    subtotal: number,
    tax: number,
    taxRate: number,
    discounts: OrderDiscount[],
    total: number,
    autofacturaUrl?: string
  ): Promise<PrintAgentResult> {
    const requestData = {
      Type: 0, // PreCheck
      Receipt: {
        RestaurantName: restaurantName,
        Address: restaurantAddress || null,
        Phone: restaurantPhone || null,
        Title: 'PRE-CUENTA',
        TableName: tableName,
        WaiterName: waiterName,
        DateTime: getLocalDateTimeString(),
        Items: groupOrderItems(order.orderItems.filter(item => !item.isCancelled))
          .map(({ item, qty, total: itemTotal }) => ({
            Quantity: qty,
            Name: item.productName,
            UnitPrice: item.unitPrice,
            Total: itemTotal,
            Variants: item.variants?.map(v => {
              const name = v.quantity ? `${v.quantity} ${v.variantName}` : v.variantName;
              return v.priceModifier ? `${name} (+$${v.priceModifier.toFixed(2)})` : name;
            }) || [],
            Notes: null,
          })),
        Subtotal: subtotal,
        Tax: tax,
        TaxRate: taxRate,
        Discounts: discounts.map(d => ({
          Reason: d.reason || 'Descuento',
          Amount: d.discountAmount,
        })),
        Tip: 0,
        Total: total,
        Payments: [],
        Footer: offlineFolioFooter(order, '*** PRE-CUENTA ***\nNo es un comprobante fiscal'),
        AutofacturaUrl: autofacturaUrl || null,
        OrderNumber: order.orderNumber || null,
      },
    };

    return await this.printViaAgent(requestData);
  }

  /**
   * Print a payment receipt
   */
  async printPayment(
    order: Order,
    restaurantName: string,
    restaurantAddress: string | undefined,
    restaurantPhone: string | undefined,
    tableName: string,
    waiterName: string,
    subtotal: number,
    tax: number,
    taxRate: number,
    discounts: OrderDiscount[],
    tip: number,
    total: number,
    payments: any[],
    autofacturaUrl?: string
  ): Promise<PrintAgentResult> {
    const requestData = {
      Type: 1, // Payment
      Receipt: {
        RestaurantName: restaurantName,
        Address: restaurantAddress || null,
        Phone: restaurantPhone || null,
        Title: 'TICKET DE PAGO',
        TableName: tableName,
        WaiterName: waiterName,
        DateTime: getLocalDateTimeString(),
        Items: groupOrderItems(order.orderItems.filter(item => !item.isCancelled))
          .map(({ item, qty, total: itemTotal }) => ({
            Quantity: qty,
            Name: item.productName,
            UnitPrice: item.unitPrice,
            Total: itemTotal,
            Variants: item.variants?.map(v => {
              const name = v.quantity ? `${v.quantity} ${v.variantName}` : v.variantName;
              return v.priceModifier ? `${name} (+$${v.priceModifier.toFixed(2)})` : name;
            }) || [],
            Notes: null,
          })),
        Subtotal: subtotal,
        Tax: tax,
        TaxRate: taxRate,
        Discounts: discounts.map(d => ({
          Reason: d.reason || 'Descuento',
          Amount: d.discountAmount,
        })),
        Tip: tip,
        Total: total,
        Payments: payments.map(p => ({
          Method: p.methodId,
          Amount: p.amount,
          Reference: p.reference || null,
        })),
        Footer: offlineFolioFooter(order, 'Gracias por su visita'),
        // Delivery/TakeOut info
        IsDelivery: order.type === 'Delivery',
        IsTakeOut: order.type === 'TakeOut',
        CustomerName: order.customerName || null,
        CustomerPhone: order.customerPhone || null,
        DeliveryAddress: order.deliveryAddress || null,
        DeliveryNotes: order.deliveryNotes || null,
        AutofacturaUrl: autofacturaUrl || null,
        OrderNumber: order.orderNumber || null,
      },
    };

    return await this.printViaAgent(requestData);
  }

  /**
   * Print a kitchen order (comanda)
   */
  async printKitchenOrder(
    order: Order,
    tableName: string,
    waiterName: string,
    newItems: OrderItem[]
  ): Promise<PrintAgentResult> {
    const requestData = {
      Type: 2, // KitchenOrder
      Receipt: {
        RestaurantName: 'COCINA',
        Address: null,
        Phone: null,
        // Header for the direct/USB path. It prints all items on one ticket (no
        // per-production-center split), so it stays generic "COCINA". The backend
        // generator now renders Title as the kitchen header.
        Title: 'COCINA',
        TableName: tableName,
        WaiterName: waiterName,
        OrderNumber: order.orderNumber || null,
        DateTime: getLocalDateTimeString(),
        Items: newItems.map((item: OrderItem) => ({
          Quantity: item.quantity,
          Name: item.productName,
          UnitPrice: item.unitPrice,
          Total: item.total,
          Variants: item.variants?.map(v => {
              const name = v.quantity ? `${v.quantity} ${v.variantName}` : v.variantName;
              return v.priceModifier ? `${name} (+$${v.priceModifier.toFixed(2)})` : name;
            }) || [],
          Notes: item.notes || null,
        })),
        Subtotal: 0,
        Tax: 0,
        TaxRate: 0,
        Discounts: [],
        Tip: 0,
        Total: 0,
        Payments: [],
        Footer: offlineFolioFooter(order, '*** COMANDA ***'),
      },
    };

    return await this.printViaAgent(requestData);
  }

  /**
   * Print a cash count (arqueo) receipt
   */
  async printCashCount(
    restaurantName: string,
    userName: string,
    expectedAmount: number,
    actualAmount: number,
    variance: number,
    denominationsText: string
  ): Promise<PrintAgentResult> {
    const requestData = {
      Type: 4, // CashCount
      Receipt: {
        RestaurantName: restaurantName,
        Address: null,
        Phone: null,
        Title: 'ARQUEO DE CAJA',
        TableName: 'CAJA',
        WaiterName: userName,
        DateTime: getLocalDateTimeString(),
        Items: [],
        Subtotal: actualAmount,
        Tax: 0,
        TaxRate: 0,
        Discounts: [],
        Tip: 0,
        Total: actualAmount,
        Payments: [],
        Footer: `${denominationsText}\n------------------------------------------\nMonto Esperado: $${expectedAmount.toFixed(2)}\nMonto Contado: $${actualAmount.toFixed(2)}\nDiferencia: ${variance > 0 ? '+' : ''}$${variance.toFixed(2)}`,
      },
    };

    return await this.printViaAgent(requestData);
  }

  /**
   * Print a cash register closure receipt
   */
  async printCashRegisterClosure(
    restaurantName: string,
    userName: string,
    openTime: string,
    initialAmount: number,
    totalSales: number,
    totalTips: number,
    expectedAmount: number,
    finalAmount: number,
    variance: number,
    totalCreditCardSales: number,
    totalDebitCardSales: number,
    totalTransferSales: number,
    totalOtherSales: number,
    totalAdults: number,
    totalChildren: number,
    notes?: string
  ): Promise<PrintAgentResult> {
    const requestData = {
      Type: 5, // CashRegisterClosure
      Receipt: {
        RestaurantName: restaurantName,
        Address: null,
        Phone: null,
        Title: 'CIERRE DE CAJA',
        TableName: 'CAJA',
        WaiterName: userName,
        DateTime: getLocalDateTimeString(),
        Items: [],
        Subtotal: finalAmount,
        Tax: 0,
        TaxRate: 0,
        Discounts: [],
        Tip: 0,
        Total: finalAmount,
        Payments: [],
        Footer: `Abierta: ${openTime}\nCerrada: ${formatInTz(new Date(), activeTimeZone, 'DD/MM/YYYY HH:mm:ss')}\n\nMonto Inicial: $${initialAmount.toFixed(2)}\nTotal Ventas: $${totalSales.toFixed(2)}\nTotal Propinas: $${totalTips.toFixed(2)}\n\n--- Ventas por Método ---\nEfectivo: $${totalSales.toFixed(2)}\nT. Crédito: $${totalCreditCardSales.toFixed(2)}\nT. Débito: $${totalDebitCardSales.toFixed(2)}\nTransferencia: $${totalTransferSales.toFixed(2)}\nOtros: $${totalOtherSales.toFixed(2)}\n\n--- Comensales ---\nAdultos: ${totalAdults}\nNiños: ${totalChildren}\nTotal: ${totalAdults + totalChildren}\n\nMonto Esperado: $${expectedAmount.toFixed(2)}\nMonto Final: $${finalAmount.toFixed(2)}\nDiferencia: $${variance.toFixed(2)}${notes ? `\n\nNotas: ${notes}` : ''}`,
      },
    };

    return await this.printViaAgent(requestData);
  }

  /**
   * Print a cash movement (income/expense) receipt
   */
  async printCashMovement(
    restaurantName: string,
    userName: string,
    movementType: 'CORRECTION' | 'DROP',
    amount: number,
    reason: string,
    reference?: string
  ): Promise<PrintAgentResult> {
    const typeLabel = movementType === 'CORRECTION' ? 'INGRESO' : 'RETIRO';

    const requestData = {
      Type: 6, // CashMovement
      Receipt: {
        RestaurantName: restaurantName,
        Address: null,
        Phone: null,
        Title: 'MOVIMIENTO DE CAJA',
        TableName: 'CAJA',
        WaiterName: userName,
        DateTime: getLocalDateTimeString(),
        Items: [],
        Subtotal: amount,
        Tax: 0,
        TaxRate: 0,
        Discounts: [],
        Tip: 0,
        Total: amount,
        Payments: [],
        Footer: `Tipo: ${typeLabel}\nMotivo: ${reason}${reference ? `\nReferencia: ${reference}` : ''}\n\nComprobante de Movimiento`,
      },
    };

    return await this.printViaAgent(requestData);
  }

  /**
   * Print a simple test receipt
   */
  async printTest(): Promise<PrintAgentResult> {
    const requestData = {
      Type: 3, // Test
      Receipt: {
        RestaurantName: 'MASSRESTAURANT',
        Address: null,
        Phone: null,
        Title: 'TEST RECEIPT',
        TableName: 'Test',
        WaiterName: null,
        DateTime: getLocalDateTimeString(),
        Items: [],
        Subtotal: 0,
        Tax: 0,
        TaxRate: 0,
        Discounts: [],
        Tip: 0,
        Total: 0,
        Payments: [],
        Footer: 'Test de impresion exitoso',
      },
    };

    return await this.printViaAgent(requestData);
  }
}

// Export singleton instance
export const printAgentService = new PrintAgentService();
